var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
export { HashRouter, matchPath, Route, Routes, useLocation, useNavigate as useRouterNavigate, useParams, } from '@showroomjs/bundles/routing';
import { BrowserRouter as OriBrowserRouter, useLocation, useMatch, useNavigate as useRouterNavigate, useResolvedPath, MemoryRouter as OriMemoryRouter, UNSAFE_LocationContext, } from '@showroomjs/bundles/routing';
import { callAll, noop } from '@showroomjs/core';
import { createNameContext } from '@showroomjs/ui';
import cx from 'classnames';
import * as React from 'react';
import { loadCodeAtPath } from '../route-mapping';
import { basename } from './config';
/**
 * Hack based on https://github.com/remix-run/react-router/issues/7375#issuecomment-975431736
 */
export const MemoryRouter = (props) => (React.createElement(UNSAFE_LocationContext.Provider, { value: null },
    React.createElement(OriMemoryRouter, null, props.children)));
const RouteIsLoadingContext = createNameContext('RouteIsLoadingContext', [false, noop]);
export const useIsLoadingRoute = () => React.useContext(RouteIsLoadingContext)[0];
export const useNavigate = () => {
    const [, setIsPending] = React.useContext(RouteIsLoadingContext);
    const [isPending, setLocalIsPending] = React.useState(false);
    const isMountedRef = React.useRef(false);
    React.useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const navigate = useRouterNavigate();
    const location = useLocation();
    return {
        navigate: (to, options = {}) => {
            if (/https?:\/\//.test(to)) {
                openInNewTab(to);
                return;
            }
            setIsPending(true);
            setLocalIsPending(true);
            const urlWhenClick = location.pathname;
            Promise.race([loadCode(to), wait(1500)]).then(() => {
                setIsPending(false);
                // avoid race condition where user click another route before the Promise resolves
                if (urlWhenClick === location.pathname) {
                    navigate(to, options);
                }
                if (isMountedRef.current) {
                    setLocalIsPending(false);
                }
            });
        },
        isPending,
    };
};
export const BrowserRouter = (props) => {
    const loadingRouteState = React.useState(false);
    return (React.createElement(RouteIsLoadingContext.Provider, { value: loadingRouteState },
        React.createElement(OriBrowserRouter, Object.assign({}, props))));
};
function openInNewTab(url) {
    try {
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('target', '_BLANK');
        a.click();
    }
    catch (err) {
        window.location.href = url;
    }
}
/**
 * Custom Link component so we can preload code before navigate
 * to avoid blank screen flash
 */
export const Link = React.forwardRef(function Link(_a, ref) {
    var { to, replace, pendingClassName } = _a, props = __rest(_a, ["to", "replace", "pendingClassName"]);
    const { navigate, isPending } = useNavigate();
    return (React.createElement("a", Object.assign({}, props, { className: cx(props.className, isPending && pendingClassName), onClick: callAll(props.onClick, (ev) => {
            ev.preventDefault();
            navigate(to, {
                replace,
            });
        }), onMouseEnter: callAll(props.onMouseEnter, () => loadCodeAtPath(to)), "aria-busy": isPending || undefined, href: basename && to === '/' ? basename : `${basename}${to}`, ref: ref })));
});
export const NavLink = React.forwardRef(function NavLink(_a, ref) {
    var { exact } = _a, props = __rest(_a, ["exact"]);
    const resolved = useResolvedPath(props.to);
    const match = useMatch({ path: resolved.pathname, end: exact });
    return (React.createElement(Link, Object.assign({}, props, (match ? { 'aria-current': 'page' } : {}), { ref: ref })));
});
const loadCode = (path) => new Promise((fulfill) => {
    loadCodeAtPath(path, fulfill);
});
const wait = (ms) => new Promise((fulfill) => setTimeout(fulfill, ms));
//# sourceMappingURL=routing.js.map